/* eslint-disable react/no-array-index-key */
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Ratings as StarRating } from '@sainsburys-tech/bolt'
import { ArgosTestersLogo } from '@sainsburys-tech/bolt-icons'
import { canUseDom, agnostifyUrl } from '../../../helpers/utils'
import { createGolDataAttributes, joinLinkTitles } from '../../../helpers/tracking'
import Image from '../../../helpers/Image/Image'

import * as Styled from './Quote.styles'

const Quote = ({
  mainImage,
  images,
  rating,
  heading,
  bodyCopy,
  quoteIndex,
  name,
  blogger,
  customer,
  insideModal,
  onLinkClick,
  bodyCopyCharLimit,
  quoteModal,
  badge,
  brand
}) => {
  const [activeImg, setActiveImg] = useState(0)

  const changeActiveImg = (newActiveImg) => {
    setActiveImg(newActiveImg)
  }

  const isABot = (userAgent = '') => {
    return /bot|googlebot|crawler|spider|robot|crawling/i.test(userAgent)
  }

  let mainImageLinkUrl = mainImage && mainImage.linkUrl ? mainImage.linkUrl : ''
  // If the user agent is a bot, we remove the 'tag' query parameter from the URL to prevent SEO issues seen in Argos previously.
  const isBotOnArgos = typeof window !== 'undefined' && brand === 'argos' && isABot(navigator?.userAgent)
  if (isBotOnArgos && mainImageLinkUrl) {
    if (mainImageLinkUrl.startsWith('//')) {
      mainImageLinkUrl = `https:${mainImageLinkUrl}`
    }
    const mainImageLinkUrlObj = new URL(mainImageLinkUrl)
    mainImageLinkUrlObj.searchParams.delete('tag')
    mainImageLinkUrlObj.searchParams.delete('rec')
    mainImageLinkUrlObj.searchParams.delete('clickOrigins')
    mainImageLinkUrl = mainImageLinkUrlObj.toString()
  }

  const avatarColor = {
    argos: 'argos-red',
    sainsburys: 'sainsburys-orange',
    tu: 'black',
    habitat: 'black',
    nectar: 'nectar-purple'
  }

  const starRatingProps = {
    large: false,
    rating,
    position: 'left'
  }
  const onLinkClickFn = (e) =>
    typeof onLinkClick === 'function' ? onLinkClick(e, agnostifyUrl(mainImageLinkUrl)) : false

  const avatarImgSettings = `${
    blogger && blogger.avatar
  }?qlt=50&fmt=pjpeg&defaultimage=prod-img-placeholder&w=200&sm=aspect&aspect=1:1`
  const previewImgSettings = (imageUrl) =>
    `${imageUrl}?qlt=50&fmt=pjpeg&defaultimage=prod-img-placeholder&w=200&sm=aspect&aspect=1.5:1`

  const bodyCopySubString = bodyCopy ? bodyCopy.substring(0, bodyCopyCharLimit) : ''
  const ellipsis = bodyCopy && bodyCopy.length > bodyCopyCharLimit ? '...' : ''
  const bodyCopyShort = `${bodyCopySubString}${ellipsis} `

  const customerDetails = customer && `${customer.townName ? `${customer.townName}, ` : ''} ${customer.reviewDate}`

  const getGOLDataAttributes = (action) =>
    createGolDataAttributes(
      'isolated',
      'M022 - NA',
      action === 'Load' ? joinLinkTitles(mainImage, [mainImage.linkTitle]) : mainImage.linkTitle,
      mainImageLinkUrl,
      heading,
      action
    )

  const addGolTracking = (evt, linkTitle, linkUrl) => {
    const dataAttributes = getGOLDataAttributes(linkTitle, linkUrl)
    Object.keys(dataAttributes).forEach((attribute) =>
      evt.currentTarget.setAttribute(attribute, dataAttributes[attribute])
    )
  }

  const imageClick = (evt, linkUrl, linkTitle) => {
    if (onLinkClick) {
      onLinkClickFn(evt, agnostifyUrl(linkUrl))
    }
    addGolTracking(evt, linkTitle, linkUrl)
  }

  let wrapperProps
  let MainImageWrapper
  if (mainImageLinkUrl) {
    MainImageWrapper = 'a'
    wrapperProps = {
      onClick: imageClick,
      title: mainImage.linkTitle,
      href: agnostifyUrl(mainImageLinkUrl),
      target: mainImage.linkTarget
    }
  } else {
    MainImageWrapper = 'div'
  }

  const imagePreviewArray = mainImage && images && images.length ? [mainImage, ...images] : []
  const validPreview = imagePreviewArray && imagePreviewArray.length

  return (
    <Styled.Quote insideModal={insideModal} data-test='m022-quote'>
      {mainImage && !insideModal && (
        <Styled.MainImageWrapper data-test='m022-quote-imageWrapper'>
          <MainImageWrapper data-test='m022-quote-imageContainer' {...wrapperProps}>
            <Image data-test='m022-quote-image' src={mainImage.source} alt={mainImage.altText} />
          </MainImageWrapper>
        </Styled.MainImageWrapper>
      )}
      {insideModal && imagePreviewArray ? (
        <>
          {mainImage || (images && images.length) ? (
            <Styled.MainImageWrapper data-test='m022-quote-imageWrapper'>
              {validPreview && canUseDom() ? (
                <Image src={imagePreviewArray[activeImg].source} alt={mainImage.altText} />
              ) : (
                <Image data-test='m022-quote-image' src={mainImage.source} alt={mainImage.altText} />
              )}
            </Styled.MainImageWrapper>
          ) : null}
          {imagePreviewArray ? (
            <Styled.Images data-test='m022-quote-images'>
              {imagePreviewArray.map((image, index) => (
                <Styled.MiniImageButton
                  data-test={`m022-quote-image-miniButton-${index}`}
                  type='button'
                  key={`m022-image-key-${index}`}
                  onClick={() => changeActiveImg(index)}
                  activeImage={activeImg === index}
                >
                  <Styled.MiniImagePreview
                    data-test={`m022-quote-image-miniButton-${index}-preview`}
                    backgroundImage={`url('${previewImgSettings(image.source)}')`}
                  />
                  <div data-test={`m022-quote-image-miniButton-${index}-altText`} className='sr-only'>
                    {image.altText}
                  </div>
                </Styled.MiniImageButton>
              ))}
            </Styled.Images>
          ) : null}
        </>
      ) : null}
      <Styled.Content data-test='m022-quote-content-container'>
        <Styled.InfoWrapper data-test='m022-quote-text-container'>
          <Styled.Rating>
            <StarRating {...starRatingProps} />
          </Styled.Rating>
          <p>
            <strong data-test='m022-quote-heading'>{heading}</strong>
          </p>
          <Styled.BodyCopyWrapper data-test='m022-quote-bodyCopy-container'>
            <p data-test='m022-quote-bodyCopy'>
              {!insideModal ? bodyCopyShort : bodyCopy}
              {!insideModal && (bodyCopy.length > 150 || validPreview) ? (
                <button
                  type='button'
                  onClick={() => {
                    quoteModal(() => ({
                      mainImage,
                      images,
                      rating,
                      heading,
                      bodyCopy,
                      quoteIndex,
                      name,
                      blogger,
                      customer,
                      insideModal,
                      onLinkClick,
                      bodyCopyCharLimit,
                      quoteModal,
                      brand
                    }))
                  }}
                  data-test='m022-quote-cta-button'
                  tabIndex='0'
                >
                  See more
                </button>
              ) : null}
            </p>
          </Styled.BodyCopyWrapper>
        </Styled.InfoWrapper>
        <Styled.ReviewerWrapper data-test='m022-quote-review-container'>
          {blogger && blogger.avatar ? (
            <Styled.Avatar
              data-test='m022-quote-bloggerAvatar'
              isDefault={false}
              backgroundImage={`url('${avatarImgSettings}')`}
            />
          ) : (
            <Styled.Avatar data-test='m022-quote-defaultAvatar' isDefault backgroundColor={avatarColor[brand]}>
              <p>{name && name.match(/[a-z]/i)[0].toUpperCase()}</p>
            </Styled.Avatar>
          )}
          <Styled.Reviewer data-test='m022-quote-reviewerDetails-container'>
            {name && <Styled.ReviewerName data-test='m022-quote-reviewerName'>{name}</Styled.ReviewerName>}
            <Styled.ReviewerDetails data-test='m022-quote-reviewerSource-container'>
              {customerDetails && (
                <Styled.CustomerDetails data-test='m022-quote-customerDetails'>
                  {customerDetails}
                </Styled.CustomerDetails>
              )}
              {blogger && blogger.linkName && (
                <Styled.BloggerLinkName data-test='m022-quote-blogger-linkName'>
                  {blogger.linkName}
                </Styled.BloggerLinkName>
              )}
              {badge && badge === 'Argos Testers' && <ArgosTestersLogo />}
            </Styled.ReviewerDetails>
          </Styled.Reviewer>
        </Styled.ReviewerWrapper>
      </Styled.Content>
      {!insideModal && <Styled.QuoteBubble />}
    </Styled.Quote>
  )
}

Quote.defaultProps = {
  linkTarget: '',
  insideModal: false,
  bodyCopyCharLimit: 150,
  brand: 'argos'
}

Quote.propTypes = {
  badge: PropTypes.string,
  blogger: PropTypes.shape({
    avatar: PropTypes.string.isRequired,
    linkName: PropTypes.string.isRequired
  }),
  bodyCopy: PropTypes.string,
  bodyCopyCharLimit: PropTypes.number,
  ctaCopy: PropTypes.string,
  heading: PropTypes.string,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      source: PropTypes.string,
      altText: PropTypes.string
    })
  ),
  insideModal: PropTypes.bool,
  linkTitle: PropTypes.string,
  linkUrl: PropTypes.string,
  mainImage: PropTypes.shape({
    source: PropTypes.string,
    altText: PropTypes.string,
    linkUrl: PropTypes.string,
    linkTitle: PropTypes.string
  }),
  name: PropTypes.string,
  onLinkClick: PropTypes.func,
  linkTarget: PropTypes.string,
  quoteIndex: PropTypes.number,
  quoteModal: PropTypes.func,
  rating: PropTypes.number,
  brand: PropTypes.string
}

export default Quote
